// client/src/pages/ShopPOS/components/OpenShiftModal.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, X } from "lucide-react";

interface OpenShiftModalProps {
  show: boolean;
  cashRegisters: any[];
  isOpeningShift: boolean;
  canUseShifts: boolean;
  getCurrencySymbol: () => string;
  onOpenShift: (cashRegisterId: string, initialCash: string) => Promise<void>;
  onCancel: () => void;
}

export function OpenShiftModal({
  show,
  cashRegisters,
  isOpeningShift,
  canUseShifts,
  getCurrencySymbol,
  onOpenShift,
  onCancel,
}: OpenShiftModalProps) {
  const [selectedCashRegister, setSelectedCashRegister] = useState("");
  const [initialCash, setInitialCash] = useState("");

  if (!show) return null;

  const handleSubmit = async () => {
    if (!selectedCashRegister || !initialCash) return;
    await onOpenShift(selectedCashRegister, initialCash);
    setSelectedCashRegister("");
    setInitialCash("");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-background rounded-lg p-6 w-full max-w-md relative">
        <button
          onClick={() => {
            onCancel();
            setSelectedCashRegister("");
            setInitialCash("");
          }}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X className="h-5 w-5" />
        </button>

        {!canUseShifts ? (
          <>
            <h3 className="text-lg font-bold mb-4">
              Funcionalidad no disponible
            </h3>
            <div className="py-4 text-center">
              <p className="text-gray-700 mb-4">
                El sistema de turnos requiere un plan Profesional o Enterprise.
              </p>
              <Button onClick={() => (window.location.href = "/owner/plans")}>
                Mejorar Plan
              </Button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-lg font-bold mb-4">Abrir Nuevo Turno</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Caja Registradora
                </label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedCashRegister}
                  onChange={(e) => setSelectedCashRegister(e.target.value)}
                  disabled={cashRegisters.length === 0}
                >
                  <option value="">Selecciona una caja</option>
                  {cashRegisters.map((register) => (
                    <option key={register.id} value={register.id}>
                      {register.name}{" "}
                      {register.location ? `(${register.location})` : ""}
                    </option>
                  ))}
                </select>
                {cashRegisters.length === 0 && (
                  <p className="text-amber-600 text-sm mt-1">
                    No hay cajas registradoras. Configura una en Ajustes.
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">
                  Monto Inicial
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {getCurrencySymbol()}
                  </span>
                  <Input
                    type="number"
                    placeholder="0.00"
                    className="pl-8"
                    value={initialCash}
                    onChange={(e) => setInitialCash(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={
                  !selectedCashRegister || !initialCash || isOpeningShift
                }
              >
                {isOpeningShift ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Abriendo...
                  </>
                ) : (
                  "Abrir Turno"
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
